import { createStore, type Store } from '../lib/store.ts';
import { describeVoiceError, reportVoiceError, VoiceError } from './errors.ts';
import { fetchSpeech } from './tts.ts';

// Voorlezer voor langere teksten (samenvattingen): markdown wordt spreektekst, de tekst gaat in
// blokken naar de server (één mp3 per blok), het volgende blok wordt alvast opgehaald zodat het
// doorloopt, en het tempo is tijdens het afspelen aan te passen (playbackRate, toonhoogte gelijk).

export interface NarratorState {
  status: 'idle' | 'laden' | 'speelt' | 'pauze' | 'fout';
  /** Blok dat speelt of geladen wordt (vanaf 0). */
  index: number;
  total: number;
  /** Bij status 'fout': één zin voor Jeroen (describeVoiceError), nooit de ruwe melding. */
  error: string | null;
}

/** play() die de browser afbreekt omdat er pauze of stop kwam: geen fout. */
const interrupted = (e: unknown) => e instanceof Error && e.name === 'AbortError';

export interface Narrator {
  state: Store<NarratorState>;
  /** Begint vooraan met deze (platte) tekst; een lopende voorlezing stopt eerst. */
  play(text: string): void;
  pause(): void;
  resume(): void;
  stop(): void;
  setRate(rate: number): void;
  rate(): number;
}

export const NARRATOR_RATES: readonly number[] = [0.8, 1, 1.25, 1.5, 1.75, 2];
export const RATE_KEY = 'jerome2.narrator.rate';

/** Tempo uit localStorage (standaard 1×). */
export function loadRate(): number {
  try {
    const v = Number(localStorage.getItem(RATE_KEY));
    return NARRATOR_RATES.includes(v) ? v : 1;
  } catch {
    return 1;
  }
}

export function saveRate(rate: number): void {
  try {
    localStorage.setItem(RATE_KEY, String(rate));
  } catch {
    // opslag niet beschikbaar
  }
}

/** Opmaak binnen een regel weg: links en afbeeldingen naar hun tekst, vet/cursief/code kaal, html en losse links weg. */
function inline(s: string): string {
  return s
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/<[^>]+>/g, '')
    .replace(/https?:\/\/\S+/g, '')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/(^|\s)(\*|_)(\S[^*_]*?)\2(?=\s|[.,;:!?]|$)/g, '$1$3')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

const sentence = (s: string): string => {
  const t = s.trim();
  if (!t) return '';
  return /[.!?:;…]$/.test(t) ? t : `${t}.`;
};

/** Markdown naar spreektekst: kopjes en lijstpunten als zinnen, tabellen als regels, opmaak weg. */
export function markdownToSpeech(md: string): string {
  const lines = md.replace(/\r\n?/g, '\n').replace(/^\s*```[^\n]*$/gm, '').split('\n');
  const out: string[] = [];
  let paragraph: string[] = [];
  const flush = () => {
    if (paragraph.length) out.push(paragraph.join(' '));
    paragraph = [];
  };
  for (const raw of lines) {
    const line = raw.replace(/^\s*>\s?/, '');
    if (!line.trim()) {
      flush();
      continue;
    }
    if (/^\s*([-*_]\s*){3,}$/.test(line)) {
      flush();
      continue;
    }
    if (line.includes('|') && /^[\s|:-]+$/.test(line) && line.includes('-')) continue;
    const heading = /^\s{0,3}#{1,6}\s+(.*)$/.exec(line);
    if (heading) {
      flush();
      out.push(sentence(inline(heading[1]!)));
      continue;
    }
    if (/^\s*\|/.test(line)) {
      const cells = line.split('|').map((c) => inline(c)).filter(Boolean);
      flush();
      if (cells.length) out.push(sentence(cells.join(', ')));
      continue;
    }
    const item = /^\s*(?:[-*+]|\d+[.)])\s+(.*)$/.exec(line);
    if (item) {
      flush();
      out.push(sentence(inline(item[1]!)));
      continue;
    }
    const text = inline(line);
    if (text) paragraph.push(text);
  }
  flush();
  return out.filter(Boolean).join('\n\n');
}

/** Een te lange alinea in stukken: op zinnen, en een te lange zin op spaties (nooit midden in een woord). */
function splitLong(p: string, max: number): string[] {
  const out: string[] = [];
  let cur = '';
  const add = (s: string) => {
    if (!cur) cur = s;
    else if (cur.length + 1 + s.length <= max) cur = `${cur} ${s}`;
    else {
      out.push(cur);
      cur = s;
    }
  };
  for (const s of p.split(/(?<=[.!?…])\s+/)) {
    if (s.length <= max) {
      add(s);
      continue;
    }
    let rest = s;
    while (rest.length > max) {
      const cut = rest.lastIndexOf(' ', max);
      const at = cut > 0 ? cut : max;
      add(rest.slice(0, at).trim());
      rest = rest.slice(at).trim();
    }
    if (rest) add(rest);
  }
  if (cur) out.push(cur);
  return out;
}

/** Verdeelt spreektekst in blokken van hoogstens `max` tekens, langs alinea's en zinnen. */
export function splitForSpeech(text: string, max = 3500): string[] {
  const blocks: string[] = [];
  let cur = '';
  const push = (piece: string) => {
    if (!piece) return;
    if (!cur) cur = piece;
    else if (cur.length + 2 + piece.length <= max) cur = `${cur}\n\n${piece}`;
    else {
      blocks.push(cur);
      cur = piece;
    }
  };
  for (const p of text.split(/\n{2,}/).map((x) => x.trim()).filter(Boolean)) {
    if (p.length <= max) push(p);
    else for (const piece of splitLong(p, max)) push(piece);
  }
  if (cur) blocks.push(cur);
  return blocks;
}

export interface NarratorOptions {
  fetchAudio?: (text: string, signal?: AbortSignal) => Promise<Blob>;
  rate?: number;
  /** Voor tests: een eigen audio-element. */
  makeAudio?: () => HTMLAudioElement;
  /** Maximale blokgrootte in tekens (standaard 3500, de grens van de server ligt op 4000). */
  blockChars?: number;
}

export function createNarrator(opts: NarratorOptions = {}): Narrator {
  const fetchAudio = opts.fetchAudio ?? fetchSpeech;
  const makeAudio = opts.makeAudio ?? (() => new Audio());
  const blockChars = opts.blockChars ?? 3500;
  let rate = opts.rate ?? 1;
  const state = createStore<NarratorState>({ status: 'idle', index: 0, total: 0, error: null });
  let session = 0;
  let controller: AbortController | null = null;
  let current: HTMLAudioElement | null = null;
  let currentUrl: string | null = null;
  let finishCurrent: (() => void) | null = null;

  const release = () => {
    const done = finishCurrent;
    finishCurrent = null;
    if (current) {
      current.onended = null;
      current.onerror = null;
      try {
        current.pause();
      } catch {
        // al gestopt
      }
      current.src = '';
      current = null;
    }
    if (currentUrl) {
      try {
        URL.revokeObjectURL(currentUrl);
      } catch {
        // geen object-url
      }
      currentUrl = null;
    }
    done?.();
  };

  const failed = (e: unknown) => {
    release();
    reportVoiceError(e, 'speech');
    state.patch({ status: 'fout', error: describeVoiceError(e, 'speech') });
  };

  async function run(blocks: string[], mySession: number, signal: AbortSignal): Promise<void> {
    const start = (i: number): Promise<Blob> => {
      const p = fetchAudio(blocks[i]!, signal);
      p.catch(() => undefined);
      return p;
    };
    let next = start(0);
    for (let i = 0; i < blocks.length; i++) {
      if (mySession !== session) return;
      state.patch({ status: 'laden', index: i, error: null });
      let blob: Blob;
      try {
        blob = await next;
      } catch (e) {
        if (mySession === session) failed(e);
        return;
      }
      if (mySession !== session) return;
      if (i + 1 < blocks.length) next = start(i + 1);
      const ok = await new Promise<boolean>((resolve) => {
        release();
        const audio = makeAudio();
        current = audio;
        finishCurrent = () => resolve(false);
        let url = '';
        try {
          url = URL.createObjectURL(blob);
        } catch {
          url = '';
        }
        currentUrl = url || null;
        audio.src = url;
        if ('preservesPitch' in audio) audio.preservesPitch = true;
        audio.playbackRate = rate;
        audio.onended = () => {
          finishCurrent = null;
          resolve(true);
        };
        audio.onerror = () => {
          finishCurrent = null;
          failed(new VoiceError('playback', 'audio kon niet worden afgespeeld'));
          resolve(false);
        };
        state.patch({ status: 'speelt', index: i });
        audio.play().catch((e: unknown) => {
          // Afgebroken door pauze of stop: resume() of release() rondt dit blok af.
          if (interrupted(e)) return;
          finishCurrent = null;
          failed(e);
          resolve(false);
        });
      });
      if (!ok || mySession !== session) return;
    }
    release();
    state.set({ status: 'idle', index: 0, total: 0, error: null });
  }

  const stop = () => {
    session += 1;
    controller?.abort();
    controller = null;
    release();
    state.set({ status: 'idle', index: 0, total: 0, error: null });
  };

  return {
    state,
    play(text) {
      stop();
      const blocks = splitForSpeech(text, blockChars);
      if (!blocks.length) return;
      session += 1;
      controller = new AbortController();
      state.set({ status: 'laden', index: 0, total: blocks.length, error: null });
      void run(blocks, session, controller.signal);
    },
    pause() {
      if (!current || state.get().status !== 'speelt') return;
      current.pause();
      state.patch({ status: 'pauze' });
    },
    resume() {
      if (!current || state.get().status !== 'pauze') return;
      state.patch({ status: 'speelt' });
      current.play().catch((e: unknown) => {
        if (!interrupted(e)) failed(e);
      });
    },
    stop,
    setRate(r) {
      rate = r;
      if (current) current.playbackRate = r;
    },
    rate: () => rate,
  };
}
